<template>
  <div class="product-variations">
    <div class="d-flex align-center">
      <span class="font-weight-bold text-title">{{
        $t("product_variants")
      }}</span>
      <v-spacer></v-spacer>
      <v-btn
        small
        color="primary"
        @click="variants.length > 0 ? (showOptionsModal = true) : editOptions()"
      >
        <v-icon left size="16">fal fa-pen</v-icon>
        {{ variants.length > 0 ? $t("edit_variations") : $t("add_variation") }}
      </v-btn>
    </div>

    <div v-if="options.length === 0">
      <v-card class="mt-4 text-center" flat>
        <v-card-text class="text-center d-flex flex-column align-center">
          <v-avatar size="80" color="grey" class="mb-2">
            <v-icon size="40" color="white">fal fa-info</v-icon>
          </v-avatar>
          <p class="text-h6 text-center grey--text mb-0 mt-2">
            {{ $t("no_options_defined") }}
          </p>
        </v-card-text>
      </v-card>
    </div>
    <div v-else>
      <div class="mb-2">
        <span class="text-body-2">{{ $t("options") }}:</span>
        <span v-for="(opt, idx) in options" :key="idx">
          {{ opt.name }}<span v-if="idx < options.length - 1"> / </span>
        </span>
      </div>
    </div>
    <div v-if="variants.length">
      <v-simple-table>
        <thead>
          <tr>
            <th width="25%">{{ $t("sku") }}</th>
            <th v-if="!recipeMode" width="25%">{{ $t("cost") }}</th>
            <th width="25%">{{ $t("price_sale") }}</th>
            <th v-if="recipeMode" width="18%">{{ $t("recipe_cost") }}</th>
            <th v-if="trackStock && !recipeMode" width="18%">{{ $t("stock") }}</th>
            <th width="10%">{{ $t("action") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(variant, idx) in variants" :key="idx">
            <td class="text-left">
              {{ variantLabel(variant.optionValues) }}
            </td>
            <td v-if="!recipeMode">
              <CurrencyInput
                class="mt-2 text-body-1"
                v-model="variant.cost"
                label=""
                :dense="true"
                :outlined="false"
                :flat="true"
              />
            </td>
            <td>
              <CurrencyInput
                class="mt-2"
                v-model="variant.price"
                label=""
                :dense="true"
                :outlined="false"
                :flat="true"
              />
            </td>

            <td v-if="recipeMode">
              <span v-if="variantHasRecipe(variant)" class="text-body-2 font-weight-medium">
                {{ variantRecipeCost(variant) | NumberFormat }}
              </span>
              <span v-else class="text-body-2 grey--text">{{ $t("no_recipe") }}</span>
            </td>
            <td v-if="trackStock && !recipeMode">
              <CurrencyInput
                v-model="variant.stock"
                label=""
                dense
                :outlined="false"
                :flat="true"
                :disabled="!editStock"
                type="number"
              />
            </td>
            <td>
              <v-btn
                v-if="recipeMode"
                class="rounded-lg mt-2 mr-1"
                fab depressed x-small
                :title="$t('edit_recipe')"
                @click="openRecipeEditor(idx)"
              ><v-icon x-small color="primary">fal fa-pen</v-icon></v-btn>
              <v-btn
                class="rounded-lg mt-2"
                fab
                depressed
                x-small
                @click="removeVariant(idx)"
                ><v-icon x-small color="red">fal fa-trash</v-icon></v-btn
              >
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <!-- Edit Options Modal -->
    <app-custom-dialog
      v-model="showOptionsModal"
      :title="mode === 'add' ? 'add_option' : 'edit_options'"
      :footer="true"
      :width="'800px'"
      @submit="saveOptions"
      @input="
        (val) => {
          showOptionsModal = val;
        }
      "
    >
      <div slot="activator">
        <!-- Option Suggestions -->
        <div class="mb-4">
          <div class="text-subtitle-2 mb-2">{{ $t("suggested_options") }}:</div>
          <div class="d-flex flex-wrap">
            <v-chip
              v-for="suggestion in optionSuggestions"
              :key="suggestion.name"
              class="ma-1"
              color="primary"
              outlined
              @click="addSuggestion(suggestion)"
            >
              {{ suggestion.name }}
            </v-chip>
          </div>
        </div>

        <div v-for="(opt, idx) in optionDraft" :key="idx" class="mb-4">
          <v-row align="center">
            <v-col cols="4">
              <TTextField
                v-model="opt.name"
                label="option_name"
                :dense="true"
                placeholder="(Storage, Color, etc.)"
              />
            </v-col>
            <v-col cols="7">
              <Combobox
                v-model="opt.values"
                label="option_values"
                :multiple="true"
                :chips="true"
                :clearable="true"
                :dense="true"
                :outlined="true"
                :small-chips="true"
                placeholder="(White, Black, etc.)"
                :auto-save="false"
                :option-name="opt.name"
              />
            </v-col>
            <v-col cols="1">
              <v-btn
                fab
                class="rounded-lg mt-4"
                depressed
                x-small
                @click="removeOption(idx)"
                ><v-icon x-small color="red">fal fa-trash</v-icon></v-btn
              >
            </v-col>
          </v-row>
        </div>
        <v-btn text color="primary" @click="addOption"
          ><v-icon left>fal fa-plus</v-icon>{{ $t("add_option") }}</v-btn
        >
      </div>
    </app-custom-dialog>

    <VariantRecipeEditor
      v-model="showRecipeEditor"
      :variant="editingVariant"
      :items="editingItems"
      :ingredients="ingredients"
      @save="handleRecipeSave"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { myMixin } from "@/mixin/main";
import TTextField from "@/components/global/TextField";
import CurrencyInput from "@/components/CurrencyInput";
import Combobox from "@/components/global/Combobox";
import AppCustomDialog from "@/components/customDialog";
import VariantRecipeEditor from "@/components/products/VariantRecipeEditor";
import { recipeLineCost } from "@/utils/ingredientConversion";
export default {
  name: "ProductVariations",
  mixins: [myMixin],
  components: {
    TTextField,
    Combobox,
    CurrencyInput,
    AppCustomDialog,
    VariantRecipeEditor,
  },
  props: {
    productId: {
      type: [Number, String],
      required: false,
      default: null,
    },
    initialOptions: {
      type: Array,
      default: () => [],
    },
    initialVariants: {
      type: Array,
      default: () => [],
    },
    showOptions: {
      type: Boolean,
      default: false,
    },
    trackStock: {
      type: Boolean,
      default: true,
    },
    editStock: {
      type: Boolean,
      default: false,
    },
    parentProduct: {
      type: Object,
      default: () => ({}),
    },
    recipeMode: { type: Boolean, default: false },
    ingredients: { type: Array, default: () => [] },
  },
  data() {
    return {
      options: [],
      variants: [],
      optionDraft: [],
      showOptionsModal: false,
      showRecipeEditor: false,
      editingVariantIndex: null,
      loading: false,
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      mode: "edit", // 'add' or 'edit'
      optionSuggestions: [],
      availableOptions: [],
    };
  },
  computed: {
    editingVariant() {
      return this.editingVariantIndex === null ? {} : this.variants[this.editingVariantIndex] || {};
    },
    editingItems() {
      const variant = this.editingVariantIndex === null ? null : this.variants[this.editingVariantIndex];
      return (variant && variant.recipe) || [];
    },
  },
  mounted() {
    // Update variants with parent data when component is mounted
    this.$nextTick(() => {
      if (this.variants.length > 0) {
        this.updateVariantsWithParentData();
      }
    });
    // Fetch available options from backend
    this.fetchAvailableOptions();
  },
  watch: {
    trackStock: {
      immediate: true,
      handler(newVal) {
        // When trackStock changes, update variants to set stock to 0 if disabled
        if (!newVal && this.variants.length > 0) {
          this.variants.forEach((variant) => {
            variant.stock = 0;
          });
        }
      },
    },
    initialOptions: {
      immediate: true,
      handler(newVal) {
        this.options = JSON.parse(JSON.stringify(newVal || []));
        this.optionDraft = JSON.parse(JSON.stringify(newVal || []));
      },
    },
    initialVariants: {
      immediate: true,
      handler(newVal) {
        this.variants = (newVal || []).map((v) => {
          if (v.optionValues) return v;
          if (v.options) return { ...v, optionValues: v.options };
          return { ...v, optionValues: {} };
        });

        // Update variants with parent data after loading
        this.$nextTick(() => {
          if (this.variants.length > 0) {
            this.updateVariantsWithParentData();
          }
        });
      },
    },
    "parentProduct.price_buy": {
      handler(newVal) {
        // Update cost for variants that haven't been manually modified
        if (this.variants.length > 0) {
          this.variants.forEach((variant) => {
            if (!variant.cost || variant.cost === 0) {
              variant.cost = newVal || 0;
            }
          });
        }
      },
    },
    "parentProduct.price_sale": {
      handler(newVal) {
        // Update price for variants that haven't been manually modified
        if (this.variants.length > 0) {
          this.variants.forEach((variant) => {
            if (!variant.price || variant.price === 0) {
              variant.price = newVal || 0;
            }
          });
        }
      },
    },
    "parentProduct.code": {
      handler(newVal) {
        // Update barcode for variants when base code changes
        if (this.variants.length > 0) {
          this.variants.forEach((variant) => {
            if (variant.sku) {
              const barcode = newVal ? `${newVal}-${variant.sku}` : variant.sku;
              variant.barcode = barcode;
            }
          });
        }
      },
    },
    "parentProduct.options": {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.options = JSON.parse(JSON.stringify(newVal));
          this.optionDraft = JSON.parse(JSON.stringify(newVal));
        }
      },
      deep: true,
    },
    "parentProduct.variants": {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.variants = newVal.map((v) => {
            if (v.optionValues) return v;
            if (v.options) return { ...v, optionValues: v.options };
            return { ...v, optionValues: {} };
          });

          // Update variants with parent data after loading
          this.$nextTick(() => {
            this.updateVariantsWithParentData();
          });
        }
      },
      deep: true,
    },
  },
  methods: {
    variantHasRecipe(variant) {
      return Array.isArray(variant.recipe) && variant.recipe.length > 0;
    },
    variantRecipeCost(variant) {
      return this.variantHasRecipe(variant)
        ? variant.recipe.reduce((sum, item) => sum + recipeLineCost(item, this.ingredients), 0)
        : 0;
    },
    openRecipeEditor(index) {
      this.editingVariantIndex = index;
      this.showRecipeEditor = true;
    },
    handleRecipeSave(items) {
      if (this.editingVariantIndex === null) return;
      this.$set(this.variants[this.editingVariantIndex], "recipe", items);
      this.$emit("variant-recipes-updated", this.variants.reduce((out, variant) => {
        out[variant.sku || this.variantLabel(variant.optionValues)] = variant.recipe || [];
        return out;
      }, {}));
      this.$emit("variations-updated", this.variants);
      this.editingVariantIndex = null;
    },
    async fetchAvailableOptions() {
      try {
        const { data } = await this.$http.get("store_options_available");
        if (data.status) {
          this.availableOptions = data.data;
          // Convert to optionSuggestions format using the data from store_options_available
          this.optionSuggestions = this.availableOptions.map((option) => ({
            name: option.name,
            values: option.values || [],
          }));
        }
      } catch (error) {
        console.log("Error fetching available options:", error);
      }
    },

    editOptions() {
      this.optionDraft = [];
      this.showOptionsModal = true;
      this.mode = "add";
    },
    addOption() {
      this.optionDraft.push({ name: "", values: [] });
      this.mode = "add";
    },
    addSuggestion(suggestion) {
      // Check if this suggestion is already added
      const exists = this.optionDraft.find(
        (opt) => opt.name === suggestion.name,
      );
      if (!exists) {
        this.optionDraft.push({
          name: suggestion.name,
          values: [...suggestion.values],
        });
        this.mode = "add";
      }
    },
    removeOption(idx) {
      this.optionDraft.splice(idx, 1);
    },
    saveOptions() {
      // Save options and generate matrix
      const oldVariants = this.variants || [];
      this.options = JSON.parse(JSON.stringify(this.optionDraft));
      this.variants = this.generateVariants(this.options, oldVariants);

      // Update variants with current parent product data
      this.updateVariantsWithParentData();

      console.log(
        "🔍 ProductVariations - saveOptions - Generated variants:",
        this.variants,
      );
      console.log(
        "🔍 ProductVariations - saveOptions - Generated options:",
        this.options,
      );

      // Emit the variations-updated event
      this.$emit("variations-updated", this.variants);

      this.showOptionsModal = false;
    },
    generateVariants(options, oldVariants = []) {
      if (!options.length) return [];
      // Cartesian product
      const valuesArr = options.map((opt) => opt.values);
      const keys = options.map((opt) => opt.name);
      const cartesian = (arr) =>
        arr.reduce((a, b) => a.flatMap((d) => b.map((e) => [...d, e])), [[]]);
      const combos = cartesian(valuesArr);
      // Helper to create a unique key for a variant (by option values)
      const variantKey = (optionValues) =>
        keys.map((k) => optionValues[k] || "").join("||");
      // Build a map of old variants by their key
      const oldMap = {};
      for (const v of oldVariants) {
        const key = variantKey(v.optionValues || v.options || {});
        oldMap[key] = v;
      }
      // Merge old data if exists, else use defaults from parent product
      return combos.map((combo) => {
        const optionValues = Object.fromEntries(
          combo.map((val, i) => [keys[i], val]),
        );
        const key = variantKey(optionValues);
        if (oldMap[key]) {
          // Preserve all fields from the old variant
          return { ...oldMap[key], optionValues };
        } else {
          // Generate SKU from product code and option values
          const skuParts = [];
          // Add product code if available
          if (this.parentProduct.code) {
            skuParts.push(this.parentProduct.code.toUpperCase());
          }
          for (const key of keys) {
            if (optionValues[key]) {
              skuParts.push(optionValues[key].toUpperCase());
            }
          }
          const sku = skuParts.join("-");

          // Generate barcode as base barcode + SKU
          const baseBarcode = this.parentProduct.code || "";
          const barcode = baseBarcode ? `${baseBarcode}-${sku}` : sku;

          // New combination, use defaults from parent product
          return {
            optionValues,
            price: this.parentProduct.price_sale || 0,
            cost: this.parentProduct.price_buy || 0,
            sku: sku,
            barcode: barcode,
            stock: 0,
          };
        }
      });
    },
    removeVariant(idx) {
      this.variants.splice(idx, 1);
      // check for clear option
      if (this.variants.length === 0) {
        this.options = [];
        this.optionDraft = [];
      }
      this.$emit("variations-updated", this.variants);
    },
    variantLabel(optionValues) {
      if (!optionValues) return "";
      return Object.values(optionValues).join(" / ");
    },
    showSnackbar(message, color = "success") {
      this.snackbar = {
        show: true,
        message,
        color,
      };
    },
    clearData() {
      this.options = [];
      this.variants = [];
      this.optionDraft = [];
      this.showOptionsModal = false;
    },
    updateVariantsWithParentData() {
      // Update variants with current parent product data
      this.variants.forEach((variant) => {
        // Update cost if not set or is 0
        if (!variant.cost || variant.cost === 0) {
          variant.cost = this.parentProduct.price_buy || 0;
        }

        // Update price if not set or is 0
        if (!variant.price || variant.price === 0) {
          variant.price = this.parentProduct.price_sale || 0;
        }

        // Update stock based on trackStock setting
        if (!this.trackStock) {
          // If track stock is disabled, set stock to 0
          variant.stock = 0;
        } else if (
          variant.stock === undefined ||
          variant.stock === null ||
          variant.stock === ""
        ) {
          // If track stock is enabled but stock is not set, set to 0
          variant.stock = 0;
        }

        // Update barcode if SKU exists
        if (variant.sku && (!variant.barcode || variant.barcode === "")) {
          const baseBarcode = this.parentProduct.code || "";
          variant.barcode = baseBarcode
            ? `${baseBarcode}-${variant.sku}`
            : variant.sku;
        }
      });
    },
  },
};
</script>

<style scoped>
.product-variations {
  margin-bottom: 20px;
}
</style>
